"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card } from "@/components/ui/card";
export default function ArchitecturalFoundations() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const instruments = [{
    label: "CRAFT — TEXTILE",
    name: "Sustainable Sourcing Protocols",
    description: "We prioritize regenerative fibers and deadstock materials, ensuring every garment minimizes environmental impact while maintaining the highest luxury standards.",
    specs: [{
      k: "Origin",
      v: "Ethical"
    }, {
      k: "Impact",
      v: "Low"
    }, {
      k: "Quality",
      v: "High"
    }],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/sustainable_textile_sourcing.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/sustainable_textile_sourcing.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/sustainable_textile_sourcing.png-wvc-sizes"
  }, {
    label: "CRAFT — CONSTRUCTION",
    name: "Precision Garment Assembly",
    description: "Our atelier employs traditional tailoring techniques fused with modern streetwear silhouettes, resulting in durable, architectural pieces built to last a lifetime.",
    specs: [{
      k: "Stitching",
      v: "Reinforced"
    }, {
      k: "Finish",
      v: "Hand-done"
    }, {
      k: "Detail",
      v: "Expert"
    }],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/garment_construction_detail.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/garment_construction_detail.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/garment_construction_detail.png-wvc-sizes"
  }, {
    label: "CRAFT — PATTERN",
    name: "Technical Pattern Drafting",
    description: "Every design begins as a blueprint. We utilize advanced pattern-making software to ensure perfect fit, drape, and structural integrity for every body type.",
    specs: [{
      k: "Accuracy",
      v: "Sub-mm"
    }, {
      k: "Method",
      v: "Digital"
    }, {
      k: "Fit",
      v: "Custom"
    }],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/technical_pattern_making.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/technical_pattern_making.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/technical_pattern_making.png-wvc-sizes"
  }, {
    label: "CRAFT — ARCHIVE",
    name: "Digital Collection Line Sheet",
    description: "Download our latest seasonal line sheet to explore the full technical specifications, fabric compositions, and design narratives behind the current collection.",
    specs: [{
      k: "Format",
      v: "PDF"
    }, {
      k: "Access",
      v: "Open"
    }, {
      k: "Update",
      v: "2024"
    }],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/digital_line_sheet_archive.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/digital_line_sheet_archive.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/digital_line_sheet_archive.webp-wvc-sizes"
  }];
  return <section data-section-id="3385" id="architectural-foundations" className="bg-background text-foreground relative w-full py-24 md:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div ref={header.ref} className={`mb-20 md:mb-28 max-w-4xl transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-secondary-foreground mb-6">
            Studio Thorne — The Atelier
          </p>
          <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
            The architecture of style.
          </h2>
          <p className="font-sans text-secondary-foreground mt-6 max-w-xl text-base md:text-lg leading-relaxed">
            Every piece is engineered with clinical precision, blending sustainable innovation with the raw aesthetic of modern streetwear.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {instruments.map((item, i) => {
          const reveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          const offsetClass = i === 0 ? "md:mt-0" : i === 1 ? "md:mt-16" : i === 2 ? "md:-mt-8" : "md:mt-8";
          return <div key={i} data-index={i} ref={reveal.ref} className={`${offsetClass} transition-all duration-[900ms] ease-out`} style={{
            transitionDelay: reveal.isVisible ? `${i * 150}ms` : "0ms",
            opacity: reveal.isVisible ? 1 : 0,
            transform: reveal.isVisible ? "translateY(0)" : "translateY(40px)"
          }}>
                <Card className="group bg-card border border-border rounded-lg p-6 md:p-10 lg:p-12 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-lg overflow-hidden">
                  {}
                  <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-secondary">
                    <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.name} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
                    <div className="pointer-events-none absolute inset-0 bg-accent/0 transition-colors duration-500 group-hover:bg-accent/10" />
                  </div>

                  {}
                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-secondary-foreground mt-8">
                    {item.label}
                  </p>

                  {}
                  <h3 className="font-serif font-default text-2xl md:text-3xl lg:text-[2rem] leading-tight tracking-tight text-foreground mt-3">
                    {item.name}
                  </h3>

                  {}
                  <p className="font-sans text-secondary-foreground mt-4 text-base leading-relaxed">
                    {item.description}
                  </p>

                  {}
                  <div className="mt-8 pt-6 border-t border-border grid grid-cols-3 gap-4">
                    {item.specs.map((spec, j) => <div key={j} data-index={j} className="flex flex-col">
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-secondary-foreground mb-1.5">
                          {spec.k}
                        </span>
                        <span className="font-mono text-lg md:text-xl text-accent-foreground">
                          {spec.v}
                        </span>
                      </div>)}
                  </div>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}